'use client';
import { Blockquote } from '~/components/ui/Blockquote';
import { AxiosError } from 'axios';
import { isSchema, ObjectSchema, Schema, ValidationOptions } from 'joi';
import omit from 'lodash/omit';
import {
    BaseSyntheticEvent,
    createContext,
    HTMLAttributes,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef
} from 'react';
import { FieldValues, FormProvider, useForm, UseFormProps } from 'react-hook-form';
import styled from 'styled-components';
import { joiResolver } from 'validation/joiResolver';

const StyledForm = styled.form``;

const ValidationContext = createContext<{
    setRule: (name: string, rule: Schema, onlyValidate?: boolean | null | undefined) => void;
    unsetRule: (name: string) => void;
}>({
    setRule: () => {},
    unsetRule: () => {}
});

const rulesReducer = (state, action) => {
    switch (action.type) {
        case 'SET':
            return { ...state, [action.payload.name]: action.payload.rule };

        case 'UNSET':
            const { [action.payload]: deletedRule, ...rulesRest } = state;
            return rulesRest;

        default:
            return state;
    }
};

interface Props extends HTMLAttributes<HTMLFormElement> {
    onSubmit: (data: FieldValues, event?: BaseSyntheticEvent<object, any, any>) => void | Promise<any | AxiosError>;
    defaultValues?: { [x: string]: any };
    options?: UseFormProps;
    validationSchema?: ObjectSchema;
    validationOptions?: ValidationOptions;
    setDefaultValuesOnRerender?: boolean;
    resetDefaultValuesOnRerender?: boolean;
    disableErrorWarning?: boolean;
    defaultValuesDeps?: any[];
}

export const setValidationErrors = (error: any, setError: any) => {
    if (error?.response?.status === 422) {
        let validationErrors = error.response.data.errors;
        if (validationErrors)
            Object.keys(validationErrors).forEach(function (key) {
                setError(key, { type: 'manual', message: validationErrors[key][0] });
            });
    }
};

export type { Props as FormProps };

export const Form = ({
    onSubmit: _onSubmit = () => {},
    children,
    options = {},
    disableErrorWarning,
    defaultValues = {},
    validationSchema,
    validationOptions = {},
    defaultValuesDeps = [],
    resetDefaultValuesOnRerender = false,
    setDefaultValuesOnRerender = true,
    ...props
}: Props) => {
    // holds the registred rules from the inner components
    // rules are registered on render with useRegisterSchema
    const [rules, dispatch] = useReducer(rulesReducer, {});
    const omittedValues = useRef<string[]>([]);
    const setRule = (name: string, rule: Schema, onlyValidate: boolean | null | undefined = false) => {
        if (onlyValidate) omittedValues.current = [...omittedValues.current, name];
        if (name && rule) dispatch({ type: 'SET', payload: { name, rule } });
    };
    const unsetRule = (name: string) => {
        omittedValues.current = omittedValues.current.filter((n) => n !== name);
        dispatch({ type: 'UNSET', payload: name });
    };

    const resolver = useMemo(() => {
        if (isSchema(validationSchema) && Object.keys(rules).length > 0) {
            return joiResolver(validationSchema.keys(rules), validationOptions);
        }

        return joiResolver(validationSchema, validationOptions);
    }, [rules, validationSchema, validationOptions]);

    const methods = useForm({
        resolver,
        defaultValues,
        ...options
    });

    useEffect(() => {
        if (defaultValuesDeps.length > 0) {
            if (defaultValues) {
                Object.keys(defaultValues).forEach((key) => methods.setValue(key, defaultValues[key]));
            }
        }
    }, defaultValuesDeps);

    useEffect(() => {
        if (resetDefaultValuesOnRerender) methods.reset();
        if (setDefaultValuesOnRerender)
            if (defaultValues) {
                Object.keys(defaultValues).forEach(
                    (key) => defaultValues[key] && methods.setValue(key, defaultValues[key])
                );
            }
    }, [defaultValues, methods, setDefaultValuesOnRerender]);

    const onSubmit = (data, event?) => {
        let promise = _onSubmit(omit(data, omittedValues.current), event);
        if (promise) {
            promise.then((error) => {
                setValidationErrors(error, methods.setError);
            });
        }
    };

    // @ts-ignore
    const undefinedErrors: [string, { message?: string; type?: string }][] = methods.formState.errors
        ? Object.entries(methods.formState.errors).filter(
              ([key, value]: [
                  string,
                  {
                      message?: string;
                      type?: string;
                  }
              ]) => value.type === 'manual' && !Object.keys(methods.getValues()).includes(key)
          )
        : [];

    return (
        <ValidationContext.Provider value={{ setRule, unsetRule }}>
            <FormProvider {...methods}>
                <StyledForm
                    // @ts-ignore
                    onSubmit={(e) => {
                        e.stopPropagation();
                        methods.handleSubmit(onSubmit)(e);
                    }}
                    {...props}
                >
                    {!disableErrorWarning && undefinedErrors.length > 0 && (
                        <Blockquote
                            title="Bitte überprüfe deine Eingaben"
                            color="error"
                            className="tw-mb-2 tw-text-center"
                        >
                            {undefinedErrors.map(([_, val]) => val.message)}
                        </Blockquote>
                    )}

                    {children}

                    {/*{process.env.NODE_ENV !== "production" && <DevTool control={methods.control} />}*/}
                </StyledForm>
            </FormProvider>
        </ValidationContext.Provider>
    );
};

export const useRegisterSchema = (name: string, rule: Schema, onlyValidate: boolean | null | undefined = false) => {
    const { setRule, unsetRule } = useContext(ValidationContext);

    useEffect(() => {
        if (rule) {
            setRule(name, rule, onlyValidate);

            return () => {
                unsetRule(name);
            };
        }
    }, [rule, name]);
};
